import { useEffect, useState } from 'react';
import { AlarmClock, AlertTriangle, Check, ClipboardList, Eye, FilePenLine, FileText, Hourglass, Paperclip, Trash2, X, Camera } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { uploadFile } from '../lib/upload';
import { FileDrop } from './FileDrop';
import { Module } from './ui/Module';
import { EstimatorInvoiceCheck } from './EstimatorInvoiceCheck';
import { RChip } from './ui/RChip';

export type InfoPendingItem = {
  id: string; label: string; source_stage: string; status: 'waiting' | 'uploaded';
  file_id: string | null; file_name: string | null; created_by: string | null;
  uploaded_by: string | null; created_at: string; uploaded_at: string | null;
  next_follow_up_at: string | null; next_follow_up_by: string | null;
  /**
   * LO QUE EL CLIENTE YA SUBIÓ POR EL PORTAL. El item se queda en `waiting` a propósito (hay 20
   * lugares que preguntan por ese status y cambiarlo haría avanzar el claim sin que nadie mire el
   * archivo), así que sin estos campos el archivo era invisible acá: la caja decía "Waiting 12d" con
   * el hueco vacío y el cliente se quedaba para siempre en "lo estamos revisando".
   */
  received_at: string | null; received_via: string | null; reviewed_at: string | null;
  /** ClaimCam (2026-10-10): pedido de FOTO con el tag; se cumple solo al llegar una foto con ese tag. */
  kind?: 'file' | 'photo'; photo_tag?: string | null; photo_ids?: string[];
};

function daysSince(iso: string): number {
  return Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
}

/** Semáforo del follow-up PROPIO del documento: vencido (rojo) · hoy (ámbar) · futuro (normal). */
function fuState(iso: string | null): 'none' | 'overdue' | 'today' | 'future' {
  if (!iso) return 'none';
  const day = iso.slice(0, 10);
  const today = new Date().toISOString().slice(0, 10);
  return day < today ? 'overdue' : day === today ? 'today' : 'future';
}
const FU_COLOR: Record<string, { bg: string; fg: string }> = {
  overdue: { bg: 'var(--red-soft, #fee2e2)', fg: 'var(--red)' },
  today: { bg: 'var(--orange-soft, #fef3c7)', fg: 'var(--orange, #b45309)' },
  future: { bg: 'var(--surface-2)', fg: 'var(--muted)' },
};

const isImage = (name: string | null) => !!name && /\.(png|jpe?g|gif|webp|heic|bmp)$/i.test(name);
const isPdf = (name: string | null) => !!name && /\.pdf$/i.test(name);

/**
 * Preview del archivo subido, SIN embeber un visor pesado: si es imagen muestra un
 * thumbnail (click → abre completo en otra pestaña); si no, un botón "Ver".
 */
function UploadedPreview({ fileId, fileName, onOpen }: { fileId: string; fileName: string | null; onOpen: (id: string) => void }) {
  const { t } = useI18n();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let ok = true;
    if (isImage(fileName)) api.get<{ url: string }>(`/api/files/${fileId}/url`).then((r) => { if (ok) setUrl(r.url); }).catch(() => {});
    return () => { ok = false; };
  }, [fileId, fileName]);
  if (isImage(fileName)) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {url
          ? <img src={url} alt={fileName ?? ''} onClick={() => onOpen(fileId)}
              style={{ height: 90, maxWidth: 160, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--line)', cursor: 'pointer' }} />
          : <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>…</span>}
        <button className="btn ghost sm" onClick={() => onOpen(fileId)}><Eye size={14} strokeWidth={1.75} /> {t('ip.view')}</button>
      </div>
    );
  }
  return (
    <button className="btn ghost sm" onClick={() => onOpen(fileId)}>
      {isPdf(fileName) ? <FileText size={14} strokeWidth={1.75} /> : <Paperclip size={14} strokeWidth={1.75} />} {t('ip.view')}{fileName ? ` · ${fileName}` : ''}
    </button>
  );
}

/**
 * "Info Pending": items que faltan para seguir estimando. Se agregan uno a uno
 * (roof photos, tarp invoice…). Claims/Supplementing suben el archivo; al subirlo
 * el item pasa a uploaded (verde). `sourceStage` es la etapa que lo pide.
 */
export function InfoPendingBox({ claimId, sourceStage, compact, collapsible, companycamUrl, tone, estimatorInclude }: {
  claimId: string;
  sourceStage: string;
  compact?: boolean;
  collapsible?: boolean;
  /** Review / First Estimate (dueño 2026-10-07): cada ítem con archivo lleva el check "Include in the estimate". */
  estimatorInclude?: boolean;
  /** Link del álbum de fotos (CompanyCam) del claim: se ofrece arriba de los pendientes, porque lo
   *  que más se pide son fotos y así se abren de una (Paula 2026-09-15). */
  companycamUrl?: string | null;
  /** 'yellow': los pendientes se ven en una caja amarilla clara apenas se abre (Paula 2026-09-15). */
  tone?: 'yellow';
  /** Oculta el chat de "Internal notes" del final. Lo usa la tarjeta del estimador (First
   *  Estimate / Mitigation), donde el dueño lo sacó: ahí ya está "Supplements Department
   *  Updates" y dos cajas de escribir en la misma tarjeta son dos lugares donde buscar lo mismo. */
}) {
  const { t } = useI18n();
  const [items, setItems] = useState<InfoPendingItem[]>([]);
  const [label, setLabel] = useState('');
  // Tipo del pedido: archivo (como siempre) o FOTO con el tag de ClaimCam (dueño 2026-10-10: "déjame seleccionar si
  // es una foto y el tipo; al contratista le sale 'foto de gutters', abre la cámara con la etiqueta puesta").
  const [kind, setKind] = useState<'file' | 'photo'>('file');
  const [photoTag, setPhotoTag] = useState('');
  const [tags, setTags] = useState<Array<{ key: string; label_en: string; label_es: string; group_key: string }>>([]);
  const { lang } = useI18n();
  useEffect(() => { if (kind === 'photo' && tags.length === 0) api.get<{ tags: typeof tags }>('/api/claimcam/catalog').then((r) => setTags(r.tags)).catch(() => {}); }, [kind, tags.length]);
  const tagLabel = (k: string | null | undefined) => { const x = tags.find((y) => y.key === k); return x ? (lang === 'es' ? x.label_es : x.label_en) : (k ?? '').replace(/_/g, ' '); };
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // El ERROR va aparte del aviso de éxito y se pinta en rojo. Antes todo caía en el mismo texto
  // gris de 11px al final del cuadro: cuando el add fallaba, en la pantalla no pasaba nada
  // visible y se leía como "el botón no funciona".
  const [err, setErr] = useState<string | null>(null);

  /** Traduce el código del backend a algo que se pueda leer y accionar. */
  const explain = (e: unknown): string => {
    const code = e instanceof ApiError ? e.message : 'Error';
    if (code === 'label_too_long_or_empty') return t('ip.errTooLong');
    if (code === 'invalid_body') return t('ip.errTooLong');
    if (/forbidden|role/i.test(code)) return t('ip.errPerm');
    return `${t('ip.errGeneric')} (${code})`;
  };

  // El GET también avisa si falla: si se lo tragaba, el item se creaba pero la lista no se
  // refrescaba y el resultado en pantalla era idéntico a que el botón no hiciera nada.
  const load = () => api.get<{ items: InfoPendingItem[] }>(`/api/claims/${claimId}/info-pending`)
    .then((r) => { setItems(r.items); setErr(null); })
    .catch((e) => setErr(explain(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [claimId]);

  async function add() {
    if (!label.trim() || (kind === 'photo' && !photoTag)) return; setBusy(true); setMsg(null); setErr(null);
    try { await api.post(`/api/claims/${claimId}/info-pending`, { label: label.trim(), sourceStage, kind, photo_tag: kind === 'photo' ? photoTag : null }); setLabel(''); setPhotoTag(''); await load(); }
    catch (e) { setErr(explain(e)); } finally { setBusy(false); }
  }
  // Edición inline del texto (pedido 2026-08-14: la nota al CO solo se podía borrar y re-crear).
  const [editId, setEditId] = useState<string | null>(null);
  const [editVal, setEditVal] = useState('');
  async function saveLabel() {
    if (!editId || !editVal.trim()) return; setBusy(true); setErr(null);
    try { await api.patch(`/api/claims/${claimId}/info-pending/${editId}`, { label: editVal.trim() }); setEditId(null); await load(); }
    catch (e) { setErr(explain(e)); } finally { setBusy(false); }
  }
  async function del(id: string) {
    if (!window.confirm(t('ip.delConfirm'))) return; setBusy(true);
    try { await api.del(`/api/claims/${claimId}/info-pending/${id}`); await load(); }
    catch (e) { setErr(explain(e)); } finally { setBusy(false); }
  }
  async function upload(id: string, file: File) {
    setBusy(true); setMsg(null);
    try {
      const key = await uploadFile(file, 'misc');
      await api.post(`/api/claims/${claimId}/info-pending/${id}/upload`, { fileKey: key, fileName: file.name, mimeType: file.type });
      setMsg(t('ip.uploaded')); await load();
    } catch (e) { setErr(explain(e)); } finally { setBusy(false); }
  }
  async function download(fileId: string) {
    try { const r = await api.get<{ url: string }>(`/api/files/${fileId}/url`); window.open(r.url, '_blank', 'noopener'); } catch { /* */ }
  }
  /** Follow-up PROPIO del documento: se guarda al elegir la fecha (sin botón extra). */
  async function saveItemFu(id: string, date: string) {
    setBusy(true); setMsg(null);
    try { await api.post(`/api/claims/${claimId}/info-pending/${id}/follow-up`, { date: date || null }); await load(); }
    catch (e) { setErr(explain(e)); } finally { setBusy(false); }
  }
  /**
   * EL SEGUNDO PASO DEL ARCHIVO QUE SUBIÓ EL CLIENTE — y el que cierra el círculo.
   *
   * El endpoint existía desde el primer día y NO TENÍA NINGÚN LLAMADOR: sin este botón, el archivo
   * que el cliente sube por el portal se queda para él en "lo estamos revisando" para siempre y para
   * la oficina en "Waiting Nd". `aprobado: true` pasa el item a uploaded; `false` lo devuelve a
   * pendiente (limpia `received_at`) y el ciclo de recordatorios sigue.
   *
   * Pide `manager`+ del lado del servidor, así que a un `office` le contesta 403: `explain` ya lo
   * traduce a "no tenés permiso" en vez de dejar un botón que parece no hacer nada.
   */
  async function revisado(id: string, aprobado: boolean) {
    if (!aprobado && !window.confirm(t('ip.returnConfirm'))) return;
    setBusy(true); setMsg(null); setErr(null);
    try {
      await api.post(`/api/client-portal/items/${id}/revisado`, { aprobado });
      setMsg(t(aprobado ? 'ip.reviewedOk' : 'ip.returnedOk'));
      await load();
    } catch (e) { setErr(explain(e)); } finally { setBusy(false); }
  }

  const waiting = items.filter((i) => i.status === 'waiting');
  const uploaded = items.filter((i) => i.status === 'uploaded');
  // Llegó por el portal y NADIE lo miró todavía: es lo único accionable de la caja, así que gana el
  // chip del header y la línea de preview.
  const porRevisar = waiting.filter((i) => i.received_at);

  // Chip del header (mockup B-working-views: "1 waiting" ámbar / "All received" verde). Lo que el
  // cliente ya mandó y espera revisión va PRIMERO: es trabajo que está frenado esperando a alguien.
  const chip = porRevisar.length > 0
    ? <RChip variant="warn" icon={Eye}>{porRevisar.length} {t('ip.toReview')}</RChip>
    : items.length > 0 && uploaded.length === items.length
      ? <RChip variant="ok" icon={Check}>{t('ip.allIn')}</RChip>
      : waiting.length > 0
        ? <RChip variant="warn" icon={Hourglass}>{waiting.length} {t('ip.waiting')}</RChip>
        : undefined;
  // Preview de una línea al cerrar (principio 1: lo colapsado siempre dice qué hay).
  const preview = items.length === 0
    ? t('ip.empty')
    : porRevisar.length > 0
      ? `${porRevisar.length} ${t('ip.toReview')} · ${porRevisar.map((i) => i.label).join(' · ').slice(0, 140)}`
      : waiting.length > 0
        ? `${waiting.length} ${t('ip.waiting')} · ${waiting.map((i) => i.label).join(' · ').slice(0, 140)}`
        : t('ip.allIn');

  const body = (<>
      {/* Agregar item uno a uno. Es un textarea y no un input: acá se escribe el pedido completo
          que va a leer el contratista, no dos palabras — con una sola línea no se veía lo escrito. */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <select className="input sm" value={kind} onChange={(e) => setKind(e.target.value as 'file' | 'photo')} aria-label={t('ip.kind')} style={{ width: 'auto' }}>
          <option value="file">{t('ip.kindFile')}</option>
          <option value="photo">{t('ip.kindPhoto')}</option>
        </select>
        {kind === 'photo' && (
          <select className="input sm" value={photoTag} onChange={(e) => { setPhotoTag(e.target.value); if (!label.trim() && e.target.value) setLabel(`${tagLabel(e.target.value)} photo`); }} aria-label={t('ip.photoTag')} style={{ width: 'auto', maxWidth: 220 }}>
            <option value="">{t('ip.photoTagPh')}</option>
            {tags.map((x) => <option key={x.key} value={x.key}>{lang === 'es' ? x.label_es : x.label_en}</option>)}
          </select>
        )}
        <textarea className="input sm" rows={label.length > 70 ? 3 : 1} style={{ flex: 1, minWidth: 180, resize: 'vertical', lineHeight: 1.45 }}
          placeholder={kind === 'photo' ? t('ip.addPhotoPh') : t('ip.addPh')} value={label} maxLength={2000}
          onChange={(e) => setLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); add(); } }} />
        <button className="btn sm" disabled={busy || !label.trim() || (kind === 'photo' && !photoTag)} onClick={add}>{t('ip.add')}</button>
      </div>
      {label.length > 1500 && <div className="muted" style={{ fontSize: 'var(--fs-2xs)', textAlign: 'right' }}>{label.length} / 2000</div>}
      {/* El error, ARRIBA de la lista y en rojo: es lo que hay que ver cuando algo no salió. */}
      {err && (
        <div style={{ background: 'var(--red-soft)', color: 'var(--red)', border: '1px solid var(--red)', borderRadius: 8, padding: '6px 9px', fontSize: 'var(--fs-2xs)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>
          <AlertTriangle size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {err}
        </div>
      )}

      {/* Fotos del claim (CompanyCam) a un clic, arriba de la lista. */}
      {companycamUrl && (
        <a href={companycamUrl} target="_blank" rel="noopener noreferrer" className="btn ghost sm" style={{ justifySelf: 'start', textDecoration: 'none' }}>
          <Camera size={13} strokeWidth={1.75} /> {t('ip.openPhotos')}
        </a>
      )}
      {/* Lista de items. Con tone="yellow", lo que FALTA va en amarillo claro: es lo primero que se lee. */}
      {items.length === 0 && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('ip.empty')}</div>}
      {items.map((it) => (
        <div key={it.id} style={{ padding: '7px 9px', borderRadius: 8, display: 'grid', gap: 6,
          ...(tone === 'yellow' && it.status === 'waiting'
            ? { background: 'var(--yellow-soft, #fff7cc)', border: '1px solid var(--yellow, #e6c200)', color: 'var(--ink)' }
            : { background: 'var(--surface-2)' }) }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {it.status === 'uploaded'
              ? <RChip variant="ok" icon={Check}>{t('ip.uploadedTag')}</RChip>
              : it.received_at
                // Ya llegó: decirle "waiting 12d" a la oficina cuando el cliente cumplió hace 12
                // días es contar los días al revés — el que debe algo somos nosotros.
                ? <RChip variant="warn" icon={Eye}>{t('ip.toReview')} {daysSince(it.received_at)}d</RChip>
                : <RChip variant="warn" icon={Hourglass}>{t('ip.waiting')} {daysSince(it.created_at)}d</RChip>}
            {editId === it.id ? (
              <textarea className="input sm" autoFocus rows={2} style={{ flex: 1, minWidth: 220, resize: 'vertical', lineHeight: 1.45 }}
                value={editVal} maxLength={2000} onChange={(e) => setEditVal(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void saveLabel(); } if (e.key === 'Escape') setEditId(null); }} />
            ) : (
              <span style={{ fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-semibold)' as React.CSSProperties['fontWeight'] }}>{it.label}</span>
            )}
            {it.kind === 'photo' && <span className="badge blue" style={{ fontSize: 'var(--fs-2xs)' }}><Camera size={11} strokeWidth={1.75} /> {tagLabel(it.photo_tag)}{it.photo_ids && it.photo_ids.length > 0 ? ` · ${it.photo_ids.length}` : ''}</span>}
            {!compact && editId !== it.id && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>· {it.source_stage}</span>}
            <span style={{ flex: 1 }} />
            {it.status === 'uploaded' && it.uploaded_by && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{it.uploaded_by}</span>}
            {it.kind === 'photo' && it.photo_ids && it.photo_ids.length > 0 && <a className="btn ghost sm" href={`/claimcam/${claimId}?tag=${encodeURIComponent(it.photo_tag ?? '')}`} target="_blank" rel="noreferrer" title={t('ip.openInClaimCam')}><Camera size={13} strokeWidth={1.75} /></a>}
            {editId === it.id ? (
              <>
                <button className="btn sm" disabled={busy || !editVal.trim()} onClick={() => void saveLabel()}>{t('common.save')}</button>
                <button className="btn ghost sm" disabled={busy} onClick={() => setEditId(null)}>{t('common.cancel')}</button>
              </>
            ) : (
              <button className="btn ghost sm" disabled={busy} title={t('common.edit')} onClick={() => { setEditId(it.id); setEditVal(it.label); }}><FilePenLine size={14} strokeWidth={1.75} /></button>
            )}
            <button className="btn ghost sm" style={{ color: 'var(--red)' }} disabled={busy} title={t('ip.del')} onClick={() => del(it.id)}><Trash2 size={14} strokeWidth={1.75} /></button>
          </div>
          {/* Follow-up PROPIO de ESTE documento: cada pendiente se persigue en su ritmo.
              Rojo = ya venció · ámbar = vence hoy. Se guarda al elegir la fecha. */}
          {it.status === 'waiting' && (() => {
            const st = fuState(it.next_follow_up_at);
            const c = FU_COLOR[st];
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}><AlarmClock size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('ip.itemFu')}</span>
                <input
                  className="input sm" type="date" style={{ width: 148, fontSize: 'var(--fs-2xs)', ...(c ? { background: c.bg, color: c.fg, fontWeight: st === 'overdue' ? 700 : 400 } : {}) }}
                  value={it.next_follow_up_at ? it.next_follow_up_at.slice(0, 10) : ''}
                  disabled={busy}
                  onChange={(e) => saveItemFu(it.id, e.target.value)}
                />
                {st === 'overdue' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: FU_COLOR.overdue!.bg, color: FU_COLOR.overdue!.fg, fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>{t('ip.fuOverdue')}</span>}
                {st === 'today' && <span className="badge" style={{ fontSize: 'var(--fs-2xs)', background: FU_COLOR.today!.bg, color: FU_COLOR.today!.fg, fontWeight: 'var(--fw-bold)' as React.CSSProperties['fontWeight'] }}>{t('ip.fuToday')}</span>}
                {it.next_follow_up_at && it.next_follow_up_by && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>· {it.next_follow_up_by}</span>}
                {it.next_follow_up_at && <button className="btn ghost sm" style={{ padding: '0 5px', fontSize: 'var(--fs-2xs)' }} disabled={busy} title={t('ip.fuClear')} onClick={() => saveItemFu(it.id, '')}><X size={12} strokeWidth={1.75} /></button>}
              </div>
            );
          })()}
          {/* LO QUE LLEGÓ POR EL PORTAL, con el archivo y el botón que cierra el círculo. Va en vez
              del FileDrop: el cliente ya subió lo que le pedimos, y ofrecerle a la oficina subir otro
              archivo encima invita a tapar la prueba de que él cumplió. */}
          {it.status === 'waiting' && it.received_at && (
            <div style={{ display: 'grid', gap: 6, background: 'var(--card)', border: '1px solid var(--accent)', borderRadius: 8, padding: '7px 9px' }}>
              <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
                <Eye size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} />{' '}
                {t('ip.arrived', { via: it.received_via ?? 'portal', fecha: new Date(it.received_at).toLocaleDateString() })}
              </div>
              {it.file_id && <><UploadedPreview fileId={it.file_id} fileName={it.file_name} onOpen={download} />{estimatorInclude && <EstimatorInvoiceCheck claimId={claimId} item={{ kind: 'info_pending', id: it.id, label: it.label, amount_cents: null, file_id: it.file_id, file_name: it.file_name }} />}</>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button className="btn sm" disabled={busy} onClick={() => void revisado(it.id, true)}>
                  <Check size={14} strokeWidth={1.75} /> {t('ip.review')}
                </button>
                <button className="btn ghost sm" disabled={busy} onClick={() => void revisado(it.id, false)}>
                  <X size={14} strokeWidth={1.75} /> {t('ip.returnIt')}
                </button>
              </div>
            </div>
          )}
          {/* Subir (drag & drop o click) si está waiting; preview del archivo si ya está uploaded. */}
          {it.status === 'uploaded'
            ? (it.file_id && <><UploadedPreview fileId={it.file_id} fileName={it.file_name} onOpen={download} />{estimatorInclude && <EstimatorInvoiceCheck claimId={claimId} item={{ kind: 'info_pending', id: it.id, label: it.label, amount_cents: null, file_id: it.file_id, file_name: it.file_name }} />}</>)
            : !it.received_at && <FileDrop onFile={(f) => upload(it.id, f)} label={t('ip.dropHere')} busy={busy} busyLabel={t('cd.uploading')} />}
        </div>
      ))}

      {/* Acá vivía un segundo chat del canal Intake ("Internal notes"). Se quitó a pedido del
          dueño (2026-08-24): era el MISMO hilo que "Intake Department Updates", que ya tiene su
          módulo propio en la misma pantalla — dos cajas para escribir en el mismo lugar hacen
          dudar de en cuál escribir, y quien contestaba en una no veía la otra. Supplements ya lo
          venía ocultando con un flag, que también sobra. */}

      {msg && <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{msg}</div>}
  </>);

  // Módulo colapsable estándar del rediseño: header ícono+título+chip+caret, preview al cerrar.
  // `collapsible` (ej. Claim Review lo quiere "recogido") arranca cerrado; el resto, abierto.
  return (
    <Module id={`info-pending:${claimId}`} icon={ClipboardList} title={t('ip.title')} chip={chip}
      preview={preview} defaultOpen={!collapsible}>
      <div style={{ display: 'grid', gap: 8, paddingTop: 2 }}>{body}</div>
    </Module>
  );
}
